import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import {
  AdminProduct,
  getAdminProductById,
  updateAdminProduct,
} from '../../../services/admin-service';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { useSnackbar } from 'notistack';
import GetApp from '@mui/icons-material/GetApp';

const AdminProductDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const [product, setProduct] = useState<AdminProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form states
  const [price, setPrice] = useState<number>(0);
  const [cisp1Commission, setCisp1Commission] = useState<number>(0);
  const [resellerCommission, setResellerCommission] = useState<number>(0);
  const [useAcerEmpresasPriceRule, setUseAcerEmpresasPriceRule] = useState<boolean>(false);
  const [acerEmpresasNetPrice, setAcerEmpresasNetPrice] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [manual, setManual] = useState<string>('');
  const [errors, setErrors] = useState<{
    price?: string;
    acerEmpresasNetPrice?: string;
    cisp1Commission?: string;
    resellerCommission?: string;
  }>({});

  // Calculate final price - use the price_with_tax from API if available, otherwise calculate
  const calculateFinalPrice = () => {
    // If the product has price_with_tax from the API, use it (category-based calculation)
    if (product?.price_with_tax) {
      return product.price_with_tax;
    }

    // Fallback to local calculation with hardcoded tax ratio (for backwards compatibility)
    const TAX_RATIO = 0.09085;
    const denominator = 1 - TAX_RATIO - cisp1Commission / 100 - resellerCommission / 100;

    // Avoid division by zero or negative denominators
    if (denominator <= 0) {
      return null;
    }

    return price / denominator;
  };

  // Format price as currency
  const formatPrice = (value: number | null) => {
    if (value === null) return 'N/A';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const roundMoney = (value: number): number => {
    if (!Number.isFinite(value)) return 0;
    return Math.round((value + Number.EPSILON) * 100) / 100;
  };

  const getAcerEmpresasNetPrice = (adminProduct: AdminProduct): number => {
    const specifications = adminProduct.specifications || {};
    const overrides = specifications.acer_price_overrides || {};
    const priceList = specifications.acer_price_list || {};
    const value =
      adminProduct.acer_empresas_net_price ??
      overrides.acer_empresas_net_price ??
      priceList.acer_empresas_net_price ??
      0;

    return Number(value) || 0;
  };

  const getUseAcerEmpresasPriceRule = (adminProduct: AdminProduct): boolean => {
    const specifications = adminProduct.specifications || {};
    const overrides = specifications.acer_price_overrides || {};
    return (
      adminProduct.use_acer_empresas_price_rule ?? overrides.use_acer_empresas_price_rule ?? false
    );
  };

  // Format specification values for better display
  const formatSpecValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.join(', ');
      } else {
        // Render nested objects with proper formatting
        return (
          <Box sx={{ pl: 2 }}>
            {Object.entries(value).map(([nestedKey, nestedValue]) => (
              <Box key={nestedKey} sx={{ mb: 1 }}>
                <Typography variant="body2" component="div" fontWeight="bold">
                  {nestedKey}:
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" component="div">
                    {typeof nestedValue === 'object'
                      ? formatSpecValue(nestedValue)
                      : String(nestedValue)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        );
      }
    }

    return String(value);
  };

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Painel', path: '/admin' },
      { label: 'Produtos', path: '/admin/products' },
      { label: 'Detalhes do Produto', path: `/admin/products/${id}` },
    ]);
  }, [id, setBreadcrumbs]);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await getAdminProductById(id);

        if (data) {
          setProduct(data);
          // Initialize form fields
          setPrice(roundMoney(data.price));
          // Convert decimal commission to percentage for display
          setCisp1Commission(data.cisp1_commission ? data.cisp1_commission * 100 : 12);
          setResellerCommission(data.reseller_commission ? data.reseller_commission * 100 : 8);
          setUseAcerEmpresasPriceRule(getUseAcerEmpresasPriceRule(data));
          setAcerEmpresasNetPrice(roundMoney(getAcerEmpresasNetPrice(data)));
          setIsActive(data.is_active);
          setManual(data.manual || '');
        } else {
          enqueueSnackbar('Produto não encontrado', { variant: 'error' });
          navigate('/admin/products');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        enqueueSnackbar('Falha ao carregar produto', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, navigate, enqueueSnackbar]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (price <= 0) {
      newErrors.price = 'O preço deve ser maior que 0';
    }

    if (acerEmpresasNetPrice < 0) {
      newErrors.acerEmpresasNetPrice = 'O preço net Acer deve ser maior ou igual a 0';
    }

    if (cisp1Commission < 0 || cisp1Commission > 100) {
      newErrors.cisp1Commission = 'A comissão deve estar entre 0 e 100';
    }

    if (resellerCommission < 0 || resellerCommission > 100) {
      newErrors.resellerCommission = 'A comissão deve estar entre 0 e 100';
    }

    if (cisp1Commission + resellerCommission >= 90) {
      newErrors.cisp1Commission = 'Comissão total muito alta';
      newErrors.resellerCommission = 'Comissão total muito alta';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      enqueueSnackbar('Por favor, corrija os erros no formulário', { variant: 'error' });
      return;
    }

    if (!id) return;

    try {
      setSaving(true);

      const updatedProduct = await updateAdminProduct(id, {
        price: roundMoney(price),
        cisp1_commission: cisp1Commission / 100, // Convert percentage to decimal for API
        reseller_commission: resellerCommission / 100, // Convert percentage to decimal for API
        use_acer_empresas_price_rule: useAcerEmpresasPriceRule,
        acer_empresas_net_price: roundMoney(acerEmpresasNetPrice),
        is_active: isActive,
        manual,
      });

      if (updatedProduct) {
        setProduct(updatedProduct);
        // Update form fields with new values including updated commissions
        setPrice(roundMoney(updatedProduct.price));
        setCisp1Commission(
          updatedProduct.cisp1_commission ? updatedProduct.cisp1_commission * 100 : 12
        );
        setResellerCommission(
          updatedProduct.reseller_commission ? updatedProduct.reseller_commission * 100 : 8
        );
        setUseAcerEmpresasPriceRule(getUseAcerEmpresasPriceRule(updatedProduct));
        setAcerEmpresasNetPrice(roundMoney(getAcerEmpresasNetPrice(updatedProduct)));
        enqueueSnackbar('Produto atualizado com sucesso', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error updating product:', error);
      enqueueSnackbar('Falha ao atualizar produto', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h5" color="error">
          Produto não encontrado
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/products')}
          sx={{ mt: 2 }}
        >
          Voltar para Produtos
        </Button>
      </Container>
    );
  }

  const finalPrice = calculateFinalPrice();
  const primaryImage = product.images?.find(img => img.is_primary)?.image_url || '';

  return (
    <>
      <>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/products')}
          sx={{ mb: 2 }}
        >
          Voltar para Produtos
        </Button>

        <Grid container spacing={4}>
          {/* Product Image and Basic Info */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardMedia
                component="img"
                image={primaryImage || 'https://via.placeholder.com/300x300?text=No+Image'}
                alt={product.name}
                sx={{
                  height: 300,
                  objectFit: 'contain',
                  bgcolor: 'background.paper',
                  p: 2,
                }}
              />
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {product.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {product.description}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" component="span">
                    SKU:
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                    {product.sku}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Editable Product Details */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Detalhes de Preço
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Preço Net CISP1"
                        fullWidth
                        value={price}
                        onChange={e => setPrice(roundMoney(Number(e.target.value)))}
                        type="number"
                        inputProps={{
                          min: 0,
                          step: 0.01,
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                        }}
                        error={!!errors.price}
                        helperText={errors.price}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Preço Net Acer Empresas"
                        fullWidth
                        value={acerEmpresasNetPrice}
                        onChange={e => setAcerEmpresasNetPrice(roundMoney(Number(e.target.value)))}
                        type="number"
                        inputProps={{
                          min: 0,
                          step: 0.01,
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                        }}
                        error={!!errors.acerEmpresasNetPrice}
                        helperText={errors.acerEmpresasNetPrice}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Preço Final (c/ impostos)"
                        fullWidth
                        value={finalPrice ? formatPrice(finalPrice) : 'N/A'}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={useAcerEmpresasPriceRule}
                            onChange={e => setUseAcerEmpresasPriceRule(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Aplicar regra: Preço Acer Empresas menor que preço CISP1"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Comissão CISP1"
                        fullWidth
                        value={cisp1Commission}
                        onChange={e => setCisp1Commission(Number(e.target.value))}
                        type="number"
                        inputProps={{
                          min: 0,
                          max: 100,
                          step: 1,
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        error={!!errors.cisp1Commission}
                        helperText={errors.cisp1Commission}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Comissão do Revendedor"
                        fullWidth
                        value={resellerCommission}
                        onChange={e => setResellerCommission(Number(e.target.value))}
                        type="number"
                        inputProps={{
                          min: 0,
                          max: 100,
                          step: 1,
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        error={!!errors.resellerCommission}
                        helperText={errors.resellerCommission}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Link da Ficha Técnica"
                        fullWidth
                        value={manual}
                        onChange={e => setManual(e.target.value)}
                        placeholder="https://exemplo.com/ficha-tecnica.pdf"
                        helperText="Insira um link para a ficha técnica ou manual do produto"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isActive}
                            onChange={e => setIsActive(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Produto Ativo"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<EditIcon />}
                          onClick={() => navigate(`/admin/products/${id}/edit`)}
                        >
                          Alterar Produto
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          disabled={saving}
                        >
                          {saving ? 'Salvando...' : 'Salvar Alterações'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>

            {product.specifications && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Especificações
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {/* Manual Link */}
                  {manual && (
                    <Box sx={{ mb: 3 }}>
                      <Button
                        startIcon={<GetApp />}
                        component="a"
                        href={manual}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: '#1082ff',
                          textTransform: 'none',
                          textDecoration: 'none',
                        }}
                      >
                        Fazer download da ficha técnica
                      </Button>
                    </Box>
                  )}

                  {Object.entries(product.specifications).map(([key, value]) => (
                    <Box key={key} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" component="div" fontWeight="bold">
                        {key}:
                      </Typography>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: 'inherit',
                          mt: 0.5,
                          ml: 2,
                          p: typeof value === 'object' ? 1 : 0,
                          bgcolor: typeof value === 'object' ? 'rgba(0,0,0,0.03)' : 'transparent',
                          borderRadius: 1,
                        }}
                      >
                        {formatSpecValue(value)}
                      </Typography>
                    </Box>
                  ))}
                  {Object.keys(product.specifications).length === 0 && (
                    <Typography variant="body2">Sem especificações disponíveis</Typography>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </>
    </>
  );
};

export default AdminProductDetailsPage;
